import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle } from 'lucide-react';
import backgroundImage from '../assets/Image/sky_clouds_background.jpg';
import logo from '../assets/Logo/Emergent logo.svg';

const API = process.env.REACT_APP_BACKEND_URL || '';

function CreatorPortal() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    instagram: '',
    tiktok: '',
    youtube: '',
    facebook: '',
    other: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Check for admin password in any field (BACKEND VERIFICATION ONLY)
  const checkAdminAccess = async (value) => {
    const trimmedValue = value.trim();
    
    // Only check via backend API - NO hardcoded password in frontend
    if (trimmedValue.startsWith('/') && trimmedValue.length > 5) {
      try {
        const response = await axios.post(`${API}/api/admin/verify-access`, {
          password: trimmedValue
        }, {
          timeout: 3000 // 3 second timeout
        });
        
        if (response.data && response.data.success) {
          console.log('✅ Secret code verified, redirecting to Google OAuth...');
          // Redirect to Emergent Auth - user will return to /admin-auth-callback
          const redirectUrl = `${window.location.origin}/admin-auth-callback`;
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        }
      } catch (err) {
        // Silently fail - don't show errors to keep admin access hidden
        console.log('Access check failed');
      }
    }
  };

  // Handle field changes with admin check
  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    checkAdminAccess(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check if any field contains the admin password
    const allValues = Object.values(formData);
    const hasAdminPassword = allValues.some(val => 
      val.trim().startsWith('/') && val.trim().length > 5
    );
    
    if (hasAdminPassword) {
      // Don't submit, the admin check should have already triggered
      setError('');
      return;
    }
    
    // Check if at least one field has a value
    const hasAtLeastOne = allValues.some(val => val.trim() !== '');
    if (!hasAtLeastOne) {
      setError('Please provide at least one video URL');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/api/creator/submit-videos`, {
        instagram: formData.instagram.trim() || null,
        tiktok: formData.tiktok.trim() || null,
        youtube: formData.youtube.trim() || null,
        facebook: formData.facebook.trim() || null,
        other: formData.other.trim() || null
      });

      setIsSuccess(true);
      setFormData({ instagram: '', tiktok: '', youtube: '', facebook: '', other: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit videos. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div 
        className="min-h-screen w-full flex flex-col"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          minHeight: '100vh',
          width: '100%'
        }}
      >
        {/* Navigation Bar */}
        <nav className="w-full px-4 sm:px-6 md:px-8 py-4 md:py-6 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img src={logo} alt="Emergent Logo" className="h-8 sm:h-10 w-auto" />
          </div>
          
          {/* CTA Button */}
          <button className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-white text-black rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}>
            Try Emergent
          </button>
        </nav>

        {/* Main Content - Centered */}
        <div className="flex-1 flex items-center justify-center px-4 py-6 sm:p-6 md:p-8">
          <div className="max-w-md w-full mx-auto bg-white/90 backdrop-blur-md rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}>Successfully Submitted!</h2>
          <p className="text-gray-600 mb-6 sm:mb-8 text-base sm:text-lg" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}>
            Videos uploaded, thank you for your time!
          </p>
          <button
            onClick={() => setIsSuccess(false)}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-black hover:bg-gray-800 text-white rounded-xl transition-all font-semibold shadow-lg text-sm sm:text-base"
            style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}
          >
            Submit More Videos
          </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen w-full flex flex-col"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        width: '100%'
      }}
    >
      {/* Navigation Bar */}
      <nav className="w-full px-4 sm:px-6 md:px-8 py-4 md:py-6 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center">
          <img src={logo} alt="Emergent Logo" className="h-8 sm:h-10 w-auto" />
        </div>
        
        {/* CTA Button */}
        <button className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm sm:text-base bg-white text-black rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}>
          Try Emergent
        </button>
      </nav>

      {/* Main Content - Centered */}
      <div className="flex-1 flex items-center justify-center px-4" style={{ paddingTop: 'calc(1.5rem - 15px)', paddingBottom: '1.5rem' }}>
        <div className="max-w-xl w-full mx-auto">
          {/* Header and Subheading - Centered */}
          <div className="text-center px-4" style={{ marginBottom: '0px' }}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif', color: '#1e3a5f', letterSpacing: '-0.02em' }}>
              Content Tracker
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed px-2" style={{ fontFamily: 'Brockmann-SemiBold, sans-serif', marginBottom: '0px', color: '#2a4a6f', opacity: 0.6, fontWeight: 500 }}>
              Add your latest Emergent video URLs for the platforms you go live on. At least one URL is required.
            </p>
          </div>

        {/* Form Card - White background with backdrop blur */}
        <div 
          className="rounded-2xl sm:rounded-3xl" 
          style={{ 
            fontFamily: 'Brockmann-SemiBold, sans-serif',
            border: '2.354px solid #FFF',
            background: 'rgba(255, 255, 255, 0.20)',
            backdropFilter: 'blur(86.32608795166016px)',
            padding: '1.5rem 2rem',
            marginTop: '50px'
          }}
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Instagram */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram Link *
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => handleFieldChange('instagram', e.target.value)}
                placeholder="https://instagram.com/your-post"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>

            {/* TikTok */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                TikTok Link *
              </label>
              <input
                type="text"
                value={formData.tiktok}
                onChange={(e) => handleFieldChange('tiktok', e.target.value)}
                placeholder="https://tiktok.com/your-post"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>

            {/* YouTube */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                YouTube Link *
              </label>
              <input
                type="text"
                value={formData.youtube}
                onChange={(e) => handleFieldChange('youtube', e.target.value)}
                placeholder="https://youtube.com/your-video"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>

            {/* Facebook */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facebook Link *
              </label>
              <input
                type="text"
                value={formData.facebook}
                onChange={(e) => handleFieldChange('facebook', e.target.value)}
                placeholder="https://facebook.com/your-post"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>

            {/* Other Platform */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Other Platform Link *
              </label>
              <input
                type="text"
                value={formData.other}
                onChange={(e) => handleFieldChange('other', e.target.value)}
                placeholder="https://any-other-platform.com"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Submit Button - Black like reference */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white rounded-xl transition-all font-semibold text-lg shadow-lg hover:shadow-xl"
              style={{ fontFamily: 'Brockmann-SemiBold, sans-serif' }}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Submit Your Links'
              )}
            </button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}

export default CreatorPortal;
